import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../../../../api/axios';
import type {
  TCreateMenuItemRequest,
  TMenuItemResponse,
  TMenuListResponse,
  TUpdateMenuItemRequest,
  TUploadImageResponse,
} from '../schemas';

const MENU_URL = '/menu';
const UPLOAD_URL = '/files/upload-image';

export const QUERY_KEYS = {
  menuItems: 'menu-items',
} as const;

export const useGetMenuList = (restaurantId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.menuItems, restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<TMenuListResponse>(
        `${MENU_URL}?pageSize=300000&pageNum=1&filter__idRestaurant=${restaurantId}&orderBy=order&orderByDir=asc`,
        { headers: { 'Restaurant-ID': restaurantId } },
      );
      return response.data?.data?.rows ?? [];
    },
    enabled: !!restaurantId,
  });
};

export const useGetMenuItem = (id: string, restaurantId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.menuItems, id],
    queryFn: async () => {
      const response = await apiClient.get<TMenuItemResponse>(`${MENU_URL}/${id}`, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    enabled: !!id && !!restaurantId,
  });
};

export const useCreateMenuItem = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TCreateMenuItemRequest) => {
      const response = await apiClient.post<TMenuItemResponse>(MENU_URL, payload, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.menuItems, restaurantId] });
    },
  });
};

export const useUpdateMenuItem = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: TUpdateMenuItemRequest }) => {
      const response = await apiClient.put<TMenuItemResponse>(`${MENU_URL}/${id}`, payload, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.menuItems, restaurantId] });
    },
  });
};

export const useDeleteMenuItem = (restaurantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`${MENU_URL}/${id}`, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.menuItems, restaurantId] });
    },
  });
};

type TMenuRearrangeItem = { order: number; _idMenuItem: string };

export const useMenuRearrange = (restaurantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items: TMenuRearrangeItem[]) => {
      const response = await apiClient.put(`${MENU_URL}/rearrange`, items, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.menuItems, restaurantId] });
    },
  });
};

export const useUploadMenuImage = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('image', file);
      const response = await apiClient.post<TUploadImageResponse>(UPLOAD_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
  });
};
